import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DeleteProjectDialog } from "@/components/projects/DeleteProjectDialog";
import api, { errMsg } from "@/lib/api";
import { STAGES, STAGE_MAP } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SampleTag, StatusBadge } from "@/components/shared";
import { StageDialogs, NEEDS_DIALOG } from "@/components/projects/StageDialogs";
import { FIN_STATUS, fmtMoney } from "@/lib/constants";
import { EditProjectDialog } from "@/components/project-detail/EditProjectDialog";
import { OverviewTab } from "@/components/project-detail/OverviewTab";
import { ChecklistTab } from "@/components/project-detail/ChecklistTab";
import { CommercialsTab } from "@/components/project-detail/CommercialsTab";
import { DocumentsTab } from "@/components/project-detail/DocumentsTab";
import { ActivityTab } from "@/components/project-detail/ActivityTab";
import { DeliverablesTab } from "@/components/project-detail/DeliverablesTab";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(["overview", "checklist", "commercials", "documents", "deliverables", "activity"].includes(params.get("tab")) ? params.get("tab") : "overview");
  const { user } = useAuth();
  const canFin = ["owner", "admin", "pm", "finance"].includes(user?.role);
  const [p, setP] = useState(null);
  const [people, setPeople] = useState([]);
  const [settings, setSettings] = useState({ project_types: [], currencies: [] });
  const [clients, setClients] = useState([]);
  const [pending, setPending] = useState(null);
  const [edit, setEdit] = useState(params.get("edit") === "1");
  const [del, setDel] = useState(false);
  const [templates, setTemplates] = useState([]);

  const reload = useCallback(() => api.get(`/projects/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  const loadPeople = useCallback(() => api.get("/people").then((r) => setPeople(r.data)), []);
  useEffect(() => {
    reload();
    loadPeople();
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/checklist-templates").then((r) => setTemplates(r.data));
  }, [reload, loadPeople]);

  const applyStatus = async (status, extra = {}) => {
    try {
      await api.post(`/projects/${id}/status`, { status, ...extra });
      toast.success(`Moved to ${STAGE_MAP[status].label}`);
      reload();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const onStatus = (status) => {
    if (status === p.status) return;
    if (NEEDS_DIALOG.includes(status)) setPending({ project: p, status });
    else applyStatus(status);
  };
  const patch = async (body, msg = "Saved") => {
    try {
      await api.patch(`/projects/${id}`, body);
      toast.success(msg);
      await reload();
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
  };

  if (!p) return <div className="text-sm text-slate-500">Loading project…</div>;
  const fin = p.financials || {};
  const finMeta = FIN_STATUS[fin.status] || FIN_STATUS.not_invoiced;
  return (
    <div data-testid="project-detail-page">
      <Link to="/projects" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-3" data-testid="back-to-projects"><ArrowLeft className="h-3.5 w-3.5" /> All projects</Link>
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-slate-500" data-testid="project-code">{p.project_code}</span>
            <SampleTag show={p.is_sample} />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="project-name">{p.name}</h1>
          <div className="text-sm text-slate-500 mt-1">
            <Link to={`/clients/${p.client_id}`} className="text-teal-700 hover:underline" data-testid="project-client-link">{p.client_name}</Link> · {p.project_type}
            {p.project_manager_id && <> · PM <Link to={`/people/${p.project_manager_id}`} className="text-slate-700 font-medium hover:underline" data-testid="project-pm-link">{p.project_manager_name}</Link></>}
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs">
            <span className="flex items-center gap-1.5 text-slate-500">Status <StatusBadge status={p.status} /></span>
            {canFin && <span className="flex items-center gap-1.5 text-slate-500" data-testid="financial-status">Financial
              <span className={`px-2 py-0.5 rounded font-medium ${finMeta.cls}`}>{finMeta.label}{fin.status === "outstanding" && ` · ${fmtMoney(fin.outstanding, fin.currency)}`}{fin.overdue && " · overdue"}</span>
            </span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canFin && <Select value={p.status} onValueChange={onStatus}>
            <SelectTrigger data-testid="detail-status-select" className="w-[220px] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>}
          <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-project-button"><Pencil className="h-4 w-4 mr-1" /> Edit details</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" size="icon" data-testid="project-actions-menu"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-rose-600 focus:text-rose-700" onClick={() => setDel(true)} data-testid="delete-project-menu-item"><Trash2 className="h-4 w-4 mr-2" /> Delete project</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DeleteProjectDialog project={p} open={del} onOpenChange={setDel} onDeleted={() => navigate("/projects")} />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 h-auto p-1 mb-5">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="tab-checklist">Checklist & Team{p.checklist_stats?.total ? ` (${p.checklist_stats.completed}/${p.checklist_stats.total})` : ""}</TabsTrigger>
          {canFin && <TabsTrigger value="commercials" data-testid="tab-commercials">Commercials & Payments</TabsTrigger>}
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({p.documents.length}{p.agreements?.length ? ` + ${p.agreements.length} company` : ""})</TabsTrigger>
          <TabsTrigger value="deliverables" data-testid="tab-deliverables">Final Deliverables</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab p={p} reload={reload} onEdit={() => setEdit(true)} patch={patch} canFin={canFin} onOpenTab={setTab} /></TabsContent>
        <TabsContent value="checklist"><ChecklistTab p={p} people={people} onPersonAdded={loadPeople} patch={patch} templates={templates} reload={reload} /></TabsContent>
        {canFin && <TabsContent value="commercials"><CommercialsTab p={p} reload={reload} currencies={settings.currencies} reportingCurrency={settings.reporting_currency} patch={patch} /></TabsContent>}
        <TabsContent value="documents"><DocumentsTab p={p} reload={reload} canFin={canFin} currencies={settings.currencies} reportingCurrency={settings.reporting_currency} /></TabsContent>
        <TabsContent value="deliverables"><DeliverablesTab p={p} /></TabsContent>
        <TabsContent value="activity"><ActivityTab activities={p.activities} /></TabsContent>
      </Tabs>

      <StageDialogs pending={pending} people={people} onPersonAdded={loadPeople} templates={templates} currencies={settings.currencies} onCancel={() => setPending(null)}
        onConfirm={async (extra) => { const ok = await applyStatus(pending.status, extra); if (ok) setPending(null); }} />
      <EditProjectDialog open={edit} onOpenChange={setEdit} p={p} people={people} onPersonAdded={loadPeople} clients={clients} onClientsChanged={() => api.get("/clients").then((r) => setClients(r.data))} types={settings.project_types} onTypeAdded={(t) => setSettings((s) => ({ ...s, project_types: t }))} currencies={settings.currencies} patch={patch} currentUser={user} />
    </div>
  );
}
